import WallEntity from '@/editor/entities/wallEntity';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { globalStores } from '@/editor/stores';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { OperateAbstract } from './OperateAbstract';
import { EEntityType, EWallOperateType } from '@/editor/types';
import VirtualWallModel from '@/editor/entities/2d/virtualWallModel';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { WallMoveCalculator } from '@/editor/utils/wall/wallMoveCalculator';
import { WallPointAbsorbManager } from '@/editor/manager/absorb/wall/wallPointAbsorbManager';
import { LineSeg2d } from '@/editor/math/LineSeg2d';

export class OperateWall extends OperateAbstract<EEntityType.Wall> {
  // 只能选中一个墙体，当选择其他墙体时，之前的墙体选择状态改为没选中
  private _checkedEntity: WallEntity | null = null;
  private _startMove: boolean = false;

  private _wallPanOperation = new WallMoveCalculator(globalStores.wallStore);

  private _virtualWallModel = new VirtualWallModel();
  private _wallAbsorbManager = new WallPointAbsorbManager(globalStores.wallStore, EEntityType.Wall);

  private _startPoint: IPoint2d = { x: 0, y: 0 };
  private _verticalVector: IPoint2d = { x: 0, y: 0 };
  private _offset: number = 0;

  constructor() {
    super();
  }

  get checkedEntity() {
    return this._checkedEntity;
  }

  set checkedEntity(entity: WallEntity | null) {
    this.clear();

    if (entity) {
      this._checkedEntity = entity;
      this._wallAbsorbManager.updateWallData();
    }
  }

  override startMove(point: IPoint2d) {
    if (!this._checkedEntity) return;

    const mainWallData = globalStores.wallStore.getWallData(this._checkedEntity.id);
    if (!mainWallData) return;

    const lineSeg = new LineSeg2d(mainWallData.baseData.start, mainWallData.baseData.end);
    this._startPoint =
      mainWallData.baseData.center || lineSeg.pointOnSegment(point) ? point : lineSeg.getFootOfPerpendicular(point);

    this._wallPanOperation.setMainWall(mainWallData.id);

    const d = Point2dUtils.getUnitDirection(mainWallData.baseData.start, mainWallData.baseData.end);
    this._verticalVector = Point2dUtils.rotateDirection(d, Math.PI / 2);
  }

  completeMove(_point: IPoint2d) {
    this.moving = false;

    if (!this._checkedEntity || !this._startMove) return;

    const wallId = this._checkedEntity.id;
    const mainWallData = globalStores.wallStore.getWallData(wallId);
    if (!mainWallData) return;

    void globalStores.wallOperation.pan(wallId, this._offset).then((_isSuccess) => {
      this.clear();
      editorEvent.emit(EditorEventName.REFRESH_SELECTED);
    });
  }

  move(point: IPoint2d) {
    if (!(this.pickPoint && this._checkedEntity)) return point;
    this.moving = true;
    this._startMove = true;

    const mainWallData = globalStores.wallStore.getWallData(this._checkedEntity.id);
    if (!mainWallData) return point;

    if (!mainWallData.baseData.center) {
      // 直墙吸附
      const absorbPoint = this._wallAbsorbManager.calculateAbsorbPointByLineThenRender(
        mainWallData.baseData.start,
        mainWallData.baseData.end,
        point
      );
      if (absorbPoint) point = absorbPoint;
    }

    const moveVector = Point2dUtils.subtracted(point, this._startPoint);
    this._offset = Point2dUtils.projectScalar(moveVector, this._verticalVector);
    const result = this._wallPanOperation.compute({ type: EWallOperateType.Pan, offset: this._offset });

    // 更新虚拟墙体
    const baseWallDatas = (
      [...(result?.modifyWallInfos || []), ...(result?.otherRelativeWallInfos || [])] as {
        wallId: string;
        updateBaseData?: { start: IPoint2d; end: IPoint2d; center?: IPoint2d };
      }[]
    )
      .map((info) => {
        const wallData = globalStores.wallStore.getWallData(info.wallId);
        if (!wallData) return undefined;
        const { start, end, thickness, center, clockwise } = wallData.baseData;
        return {
          start: info.updateBaseData?.start || start,
          end: info.updateBaseData?.end || end,
          thickness,
          center: info.updateBaseData?.center || center,
          clockwise
        };
      })
      .filter((info) => !!info);

    this._virtualWallModel.update(baseWallDatas);

    return point;
  }

  clear() {
    this._checkedEntity = null;
    this._startMove = false;
    this.moving = false;

    this._virtualWallModel.clear();
    this._wallAbsorbManager.clearModel();
  }

  dispose() {
    this.clear();

    this._virtualWallModel.dispose();
    this._wallAbsorbManager.dispose();
  }
}
